import { Wand2 } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { Boton, Selector, clases } from '../../componentes/ui'
import type { Campo } from '../esquema'
import { useFormulario, useValor } from '../Formulario'
import { leer } from '../rutas'
import { Filas } from './Compuestos'
import { claseDeEntrada, Envoltura } from './Controles'
import { Casillas, Color, DeLaEstructura, FechaHora, Hora, Lugar, Mapa, PlantillaDePagina, ValorDeUbicacion } from './Datos'
import { Horario } from './Horario'
import { Traducciones } from './Traducciones'
import { Webhook } from './Webhook'
import { camposNombrables, clavePropuesta, opcionesDelProducto, respuestasDe, tituloDeVariante, variantesQueFaltan } from './referencias'

/**
 * Los controles propios que pide el núcleo con `custom.panel` en un campo (auditoría del
 * 29-09-2026). El motor sigue sin saber nada de ninguna colección: el núcleo dice «este
 * campo es la clave de un campo del formulario» o «estas son las variantes de las
 * opciones de arriba», y aquí se pinta así. Lo que se guarda sigue siendo el mismo texto,
 * y el núcleo lo vuelve a validar al recibirlo.
 *
 * - `clave`: sale sola de la etiqueta mientras se escribe, en las filas nuevas.
 * - `clave-de-campo`: un desplegable con los campos del formulario, por su etiqueta.
 * - `valor-de-campo`: las respuestas posibles del campo elegido al lado.
 * - `condicion-de-aviso`: «solo si el campo X es Y», guardado como `clave=valor`.
 * - `variantes`: «Crear todas las combinaciones» encima de la lista.
 * - `opcion-de-producto` y `valor-de-opcion`: las opciones del producto y sus valores.
 */
type Fila = Record<string, unknown>

const CONTROLES = new Set([
  'horario',
  'clave',
  'clave-de-campo',
  'valor-de-campo',
  'condicion-de-aviso',
  'variantes',
  'opcion-de-producto',
  'valor-de-opcion',
  // Los de los campos de datos y los grupos de campos (`campos/Datos.tsx`).
  'color',
  'hora',
  'fecha-hora',
  'casillas',
  'lugar',
  'mapa',
  'plantilla-de-pagina',
  'valor-de-ubicacion',
  'de-la-estructura',
  'tipo-de-contenido',
  // Los textos de lo compartido entre idiomas (§37, `campos/Traducciones.tsx`).
  'traducciones',
  // La dirección del webhook de un formulario (`campos/Webhook.tsx`).
  'webhook',
])

/** `telefono_de_contacto` → `telefonoDeContacto`: la clave de un campo de datos se lee en el tema como `doc.datos.<clave>`. */
const aCamello = (clave: string): string =>
  clave
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((parte, i) => (i === 0 ? parte.toLowerCase() : parte.charAt(0).toUpperCase() + parte.slice(1).toLowerCase()))
    .join('')
    .replace(/^[^a-z]+/, '')
    .slice(0, 40)

export const hayControlEspecial = (campo: Campo): boolean => Boolean(campo.control && CONTROLES.has(campo.control.control))

/** «campos.3.condicion.reglas.0.campo» → «campos.3.condicion.reglas.0»: la fila en la que está el campo. */
const filaDe = (ruta: string): string => ruta.split('.').slice(0, -1).join('.')

const texto = (v: unknown, alternativa = ''): string => (typeof v === 'string' ? v : alternativa)

const describe = (ruta: string, campo: Campo, error?: string) => (error ? `${ruta}-error` : campo.descripcion ? `${ruta}-ayuda` : undefined)

/** Una clave que sale de la etiqueta de su fila mientras la fila es nueva y nadie la ha escrito a mano. */
const ClaveDerivada = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valores } = useFormulario()
  const { valor, cambiar, error } = useValor<string>(ruta)
  const fila = filaDe(ruta)
  const datosDeFila = (leer(valores, fila) ?? {}) as Fila
  const desde = texto(campo.control?.desde, 'etiqueta')
  const origen = texto(datosDeFila[desde])
  // Una fila guardada tiene `id`: su clave no se toca, que con ella están guardadas las respuestas.
  const nueva = !datosDeFila.id
  const derivada = useRef<null | string>(null)
  // La etiqueta de la que salió la clave que hay: si la clave es la que saldría de ella, es
  // derivada aunque la pusiera otro (el constructor de formularios la propone al añadir el campo).
  const origenAnterior = useRef(origen)
  const nombre = ruta.split('.').at(-1) ?? ''
  const lista = leer(valores, filaDe(fila))
  const indice = Number(fila.split('.').at(-1))
  const ocupadas = (Array.isArray(lista) ? (lista as Fila[]) : []).filter((_, i) => i !== indice).map((f) => texto(f[nombre])).filter(Boolean)

  useEffect(() => {
    const anterior = origenAnterior.current
    origenAnterior.current = origen
    if (!nueva || soloLectura || !origen) return
    const actual = valor ?? ''
    // En los campos de datos la clave va en camello (`forma: 'camello'`); en los formularios, con guiones bajos.
    const proponer = (etiqueta: string) => (campo.control?.forma === 'camello' ? aCamello(clavePropuesta(etiqueta, ocupadas)) : clavePropuesta(etiqueta, ocupadas))
    const seguiaLaEtiqueta = actual === derivada.current || (anterior !== '' && actual === proponer(anterior))
    if (actual !== '' && !seguiaLaEtiqueta) return
    const propuesta = proponer(origen)
    if (propuesta === actual) return
    derivada.current = propuesta
    cambiar(propuesta)
    // Solo cuando cambia lo que se escribe en la etiqueta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origen])

  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <input
        aria-describedby={describe(ruta, campo, error)}
        aria-invalid={Boolean(error)}
        className={clases(claseDeEntrada(error), 'font-mono text-[13px]')}
        disabled={soloLectura}
        id={ruta}
        onChange={(e) => {
          derivada.current = null
          cambiar(e.target.value)
        }}
        placeholder={nueva ? 'Sale de la etiqueta' : undefined}
        value={valor ?? ''}
      />
    </Envoltura>
  )
}

/** Uno de los campos del formulario, elegido por su etiqueta. */
const ClaveDeCampo = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valores } = useFormulario()
  const { valor, cambiar, error } = useValor<string>(ruta)
  const lista = texto(campo.control?.lista, 'campos')
  // El propio campo no se ofrece: una condición sobre sí mismo no se cumple nunca.
  const propio = ruta.startsWith(`${lista}.`) ? Number(ruta.split('.')[1]) : -1
  const todos = camposNombrables(leer(valores, lista), campo.control?.tipos)
  const propioClave = propio >= 0 ? texto(((leer(valores, lista) as Fila[] | undefined) ?? [])[propio]?.clave) : ''
  const opciones = todos.filter((c) => c.clave !== propioClave)
  const actual = valor ?? ''
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <Selector aria-describedby={describe(ruta, campo, error)} aria-invalid={Boolean(error)} disabled={soloLectura} id={ruta} onChange={(e) => cambiar(e.target.value)} value={actual}>
        <option value="">Elige un campo…</option>
        {opciones.map((c) => (
          <option key={c.clave} value={c.clave}>
            {c.etiqueta}
          </option>
        ))}
        {actual && !opciones.some((c) => c.clave === actual) && <option value={actual}>«{actual}» (ya no está en el formulario)</option>}
      </Selector>
    </Envoltura>
  )
}

/** La respuesta con la que se compara: elegida entre las del campo de al lado, o escrita si no tiene opciones. */
const ValorDeCampo = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valores } = useFormulario()
  const { valor, cambiar, error } = useValor<string>(ruta)
  const hermano = texto(leer(valores, `${filaDe(ruta)}.${texto(campo.control?.hermano, 'campo')}`))
  const elegido = camposNombrables(leer(valores, texto(campo.control?.lista, 'campos'))).find((c) => c.clave === hermano)
  const respuestas = respuestasDe(elegido)
  if (respuestas.length === 0) {
    return (
      <Envoltura campo={campo} error={error} ruta={ruta}>
        <input aria-describedby={describe(ruta, campo, error)} aria-invalid={Boolean(error)} className={claseDeEntrada(error)} disabled={soloLectura} id={ruta} onChange={(e) => cambiar(e.target.value)} value={valor ?? ''} />
      </Envoltura>
    )
  }
  const actual = valor ?? ''
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <Selector aria-describedby={describe(ruta, campo, error)} aria-invalid={Boolean(error)} disabled={soloLectura} id={ruta} onChange={(e) => cambiar(e.target.value)} value={actual}>
        <option value="">Elige una respuesta…</option>
        {respuestas.map((r) => (
          <option key={r.valor} value={r.valor}>
            {r.etiqueta}
          </option>
        ))}
        {actual && !respuestas.some((r) => r.valor === actual) && <option value={actual}>«{actual}»</option>}
      </Selector>
    </Envoltura>
  )
}

/** «Avisar solo si el campo X es Y», guardado como `clave=valor` (el formato que entiende el núcleo). */
const CondicionDeAviso = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valores } = useFormulario()
  const { valor, cambiar, error } = useValor<null | string>(ruta)
  const [clave = '', esperado = ''] = (valor ?? '').split('=').map((t) => t.trim())
  const campos = camposNombrables(leer(valores, texto(campo.control?.lista, 'campos')))
  const elegido = campos.find((c) => c.clave === clave)
  const respuestas = respuestasDe(elegido)
  const poner = (c: string, v: string) => cambiar(c ? `${c}=${v}` : '')
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className="grid gap-2 sm:grid-cols-2" id={ruta}>
        <Selector aria-label="El campo" disabled={soloLectura} onChange={(e) => poner(e.target.value, '')} value={clave}>
          <option value="">Siempre</option>
          {campos.map((c) => (
            <option key={c.clave} value={c.clave}>
              Si «{c.etiqueta}» es…
            </option>
          ))}
          {clave && !elegido && <option value={clave}>«{clave}» (ya no está en el formulario)</option>}
        </Selector>
        {clave &&
          (respuestas.length > 0 ? (
            <Selector aria-label="La respuesta" disabled={soloLectura} onChange={(e) => poner(clave, e.target.value)} value={esperado}>
              <option value="">Elige una respuesta…</option>
              {respuestas.map((r) => (
                <option key={r.valor} value={r.valor}>
                  {r.etiqueta}
                </option>
              ))}
            </Selector>
          ) : (
            <input aria-label="La respuesta" className={claseDeEntrada(error)} disabled={soloLectura} onChange={(e) => poner(clave, e.target.value)} placeholder="Lo que tiene que responder" value={esperado} />
          ))}
      </div>
    </Envoltura>
  )
}

/** Las variantes, con «Crear todas las combinaciones» encima: 3 tallas × 4 colores ya no son doce filas a mano. */
const Variantes = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valores } = useFormulario()
  const { valor, cambiar } = useValor<Fila[]>(ruta)
  const filas = Array.isArray(valor) ? valor : []
  const opciones = opcionesDelProducto(leer(valores, texto(campo.control?.opciones, 'opciones')))
  const { posibles, nuevas } = variantesQueFaltan(opciones, filas)
  return (
    <div className="flex flex-col gap-2.5">
      {!soloLectura && opciones.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-caja border border-linea bg-papel-2/50 px-3 py-2.5">
          <Boton disabled={nuevas.length === 0} icono={<Wand2 />} onClick={() => cambiar([...filas, ...nuevas])} pequeno>
            {nuevas.length === 0 ? 'Están todas las combinaciones' : `Crear ${nuevas.length === posibles ? 'todas las combinaciones' : `las ${nuevas.length} que faltan`}`}
          </Boton>
          <p className="text-xs text-tenue">
            {posibles} combinacion{posibles === 1 ? '' : 'es'} con estas opciones{filas.length > 0 ? ` · ${filas.length} creada${filas.length === 1 ? '' : 's'}` : ''}. Las que ya hay no se tocan; quita las que no vendas.
            {posibles >= 200 && ' Solo se crean las 200 primeras.'}
          </p>
        </div>
      )}
      <Filas campo={campo} ruta={ruta} soloLectura={soloLectura} tituloDe={tituloDeVariante} />
    </div>
  )
}

/** Una de las opciones del producto (Talla, Color…), elegida en vez de escrita. */
const OpcionDeProducto = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valores } = useFormulario()
  const { valor, cambiar, error } = useValor<string>(ruta)
  const opciones = opcionesDelProducto(leer(valores, texto(campo.control?.lista, 'opciones')))
  const actual = valor ?? ''
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <Selector aria-invalid={Boolean(error)} disabled={soloLectura} id={ruta} onChange={(e) => cambiar(e.target.value)} value={actual}>
        <option value="">Elige…</option>
        {opciones.map((o) => (
          <option key={o.nombre} value={o.nombre}>
            {o.nombre}
          </option>
        ))}
        {actual && !opciones.some((o) => o.nombre === actual) && <option value={actual}>«{actual}» (ya no está en las opciones)</option>}
      </Selector>
    </Envoltura>
  )
}

/** Uno de los valores de la opción elegida al lado. */
const ValorDeOpcion = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valores } = useFormulario()
  const { valor, cambiar, error } = useValor<string>(ruta)
  const opcion = texto(leer(valores, `${filaDe(ruta)}.${texto(campo.control?.hermano, 'opcion')}`))
  const valoresPosibles = opcionesDelProducto(leer(valores, texto(campo.control?.lista, 'opciones'))).find((o) => o.nombre === opcion)?.valores ?? []
  const actual = valor ?? ''
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <Selector aria-invalid={Boolean(error)} disabled={soloLectura || valoresPosibles.length === 0} id={ruta} onChange={(e) => cambiar(e.target.value)} value={actual}>
        <option value="">{opcion ? 'Elige…' : 'Elige antes la opción'}</option>
        {valoresPosibles.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
        {actual && !valoresPosibles.includes(actual) && <option value={actual}>«{actual}»</option>}
      </Selector>
    </Envoltura>
  )
}

export const ControlEspecial = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  switch (campo.control?.control) {
    case 'clave':
      return <ClaveDerivada campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'clave-de-campo':
      return <ClaveDeCampo campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'valor-de-campo':
      return <ValorDeCampo campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'condicion-de-aviso':
      return <CondicionDeAviso campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'variantes':
      return <Variantes campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'opcion-de-producto':
      return <OpcionDeProducto campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'valor-de-opcion':
      return <ValorDeOpcion campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'color':
      return <Color campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'hora':
      return <Hora campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'horario':
      return <Horario campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'fecha-hora':
      return <FechaHora campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'casillas':
      return <Casillas campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'lugar':
      return <Lugar campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'mapa':
      return <Mapa campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'plantilla-de-pagina':
      return <PlantillaDePagina campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'valor-de-ubicacion':
      return <ValorDeUbicacion campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'webhook':
      return <Webhook campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'traducciones':
      return <Traducciones campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'de-la-estructura':
    case 'tipo-de-contenido':
      return <DeLaEstructura campo={campo} ruta={ruta} soloLectura={soloLectura} />
    default:
      return null
  }
}
